/**
 * @props isPopup: (boolean) - If passed as true, then component will be shown inside a modal
 * @props isPopupOpen: (boolean) - State of opening/closing of modal
 * @props onPopupCloseCb: (callback function) - Callback Function which will get called when the modal is closed
 * @props payerID - ID of the payer. If this prop is passed then component will fetch the details of this id.
 * @props payerData - We can also pass the whole object with payer details to save the API call.
 * @props fromNecGrid (boolean) - If we choose to edit payer from NEC grid page, Set it as true
 * props submitFormCb (function) - This callback function will recieve all the form fields.
 * @props loaderProps: object containing properties and methods related to loader.
    loaderProps={
        currentState: PAGE_STATE.LOADING,    // loading state string 
        loadingStates: [PAGE_STATE.LOADING, PAGE_STATE.PRELOADING]  // array of states
    }
*/

import React, { useEffect, useState } from 'react'
import StepperNavigation from "../../../../components/steppernavigation/StepperNavigation"
import GetPayerType from '../createone/getpayertype/GetPayerType';
import GetBasicInfo from '../createone/getbasicinfo/GetBasicInfo';
import GetAddressInfo from '../createone/getaddressinfo/GetAddressInfo';
import GetOptionalInfo from '../createone/getoptionalinfo/GetOptionalInfo';
import styles from "../AddPayerCommonStyles.styles"
import { PAYER_SECTION_ROUTES } from '../../../payerssection/constants';
import { useDispatch, useSelector } from 'react-redux';
import { componentKey, setAddressInformation, setAddressInformationFieldsTouched, setAllBasicInformationFields, setBasicInformationFieldsTouched, setOptionalInformation, setOptionalInformationFieldsTouched, setPayerDetailId, setPayerType, setPayerUpdationSuccessful, setZipCodeValidationSuccess } from '../AddPayerSlice';
import Loadable from '../../../../components/loadable/Loadable';
import { addNotifications } from '../../../toaster/ToasterPortalSlice';
import GeneralValidator from '../../../../../libs/utility/validators/GeneralValidator';
import { generalValidator } from '../../../../../libs/utility/validators/GeneralValidator';
import useGarbageCollector from '../../../../../libs/garbagecollector/garbageCollectorHooks';
import { PAYER_TYPE_1042_S, PAYER_TYPE_TITLE } from '../createone/getpayertype/constants';
import { setBasicInformation } from '../AddPayerSlice';
import { editPayer, getPayerDataById, validateZipCode } from '../AddPayerSaga';
import Popup from '../../../../components/popup/Popup';
import { componentKey as dataLoaderKey } from '../../../loaders/dataloader/DataLoaderSlice';
import { componentKey as authenticationSliceKey } from '../../../authentication/AuthenticationSlice';
import { VALIDATE_NAME_REGEX, VALIDATE_ZIP_CODE_REGEX } from '../../../../../libs/utility/constants';
import useTxNavigate from '../../../../../libs/txnavigate/TxNavigateHook';
import { RESTRICT_SPACE_AND_SPECIAL_CHARACTERES_REGEX, SPACE_NOT_ALLOWED_IN_CLIENT_ID_TEXT } from '../../../constants/constants';
import { ADD_PAYER_LOADING_MESSAGE_MAP, ADD_PAYER_LOADING_STATES, ADD_PAYER_SLICE_INITIAL_STATE, PAYER_SUFFIX_OPTIONS } from '../constant';
import { FEATURE_FLAG } from '../../../../featureFlags';
import Payer from '../../../../../libs/utility/Payer';
import AllStepsSingleView from '../../payerandrecipientallstepssingleview/AllStepsSingleView';
import AddPayerSuccess from '../createone/addpayersuccess/AddPayerSuccess';

export default function EditPayer({
    payerID = null,
    isPopup = false,
    isPopupOpen = false,
    onPopupCloseCb = () => { },
    payerData = {},
    fromNecGrid = false,
    submitFormCb = (formData) => { },
    onFinalSubmitCb = () => { },
    is1042SForm = false,
    payerLabel = "Payer",
    loaderProps = {},
}) {

    useGarbageCollector(componentKey);
    const navigate = useTxNavigate();
    const dispatch = useDispatch();

    const { signedInUser } = useSelector(state => state[authenticationSliceKey]);
    const editPayerData = useSelector((state) => state[componentKey]);

    const { PayerType, payerUpdationSuccessful, addressInformation, loadingState, zipCodeValidationSuccess, basicInfo, optionalInformation } = editPayerData;

    const { tinTooltip, tinPlaceholder, tinValue, tinRules, showNames, tinDisabled } = Payer.getTINProps(PayerType, basicInfo.TIN?.value);

    const { countriesList, statesList } = useSelector((state) => state[dataLoaderKey]);

    const [refreshStep, setRefreshStep] = useState(0);

    const [switchToAllStepsSingleView, setSwitchToAllStepsSingleView] = useState(false);

    // Get payer details by ID
    useEffect(() => {
        if (payerID && !payerData?.PayerDetailId) {
            dispatch(getPayerDataById({ PayerDetailId: payerID, signedInUser, is1042SForm, statesList, countriesList }))
        } else if (!payerID && !payerData?.PayerDetailId && !fromNecGrid) {
            if (isPopup) {
                onPopupCloseCb();
            } else {
                navigate(`/${PAYER_SECTION_ROUTES.PAYER}`)
            }
        }

    }, [])

    useEffect(() => {
        const USA = countriesList?.find(country => country?.Country_code === "US")
        dispatch(setAddressInformation({ PayerCountry: USA }));
    }, []);

    useEffect(() => {

        if (!payerData?.PayerDetailId && !fromNecGrid) {
            return;
        }

        if (!fromNecGrid) {
            dispatch(setPayerDetailId(payerData?.PayerDetailId))
        }

        if (is1042SForm) {
            const payerType = PAYER_TYPE_1042_S.find(r => r.value === Data?.IdType);
            if (payerType) {
                dispatch(setPayerType(payerType?.title || ""));
            }
        } else {
            dispatch(setPayerType(payerData?.IdType === "SSN" ? PAYER_TYPE_TITLE.INDIVIDUAL : PAYER_TYPE_TITLE.BUSINESS))
        }

        const PayerSuffix = PAYER_SUFFIX_OPTIONS.find(suf => suf.value === payerData.Suffix)

        dispatch(setBasicInformation({
            PayerFirstName: { value: payerData?.FirstName || "" },
            PayerMiddleName: { value: payerData?.MiddleName || "" },
            PayerLastName: { value: payerData?.LastName || "" },
            PayerSuffix: PayerSuffix ?? ADD_PAYER_SLICE_INITIAL_STATE.basicInfo.PayerSuffix,
            TIN: { value: payerData?.IdNumber || "" },
            PayerEmail: { value: payerData?.Email || "" },
            PayerPhNo: { value: payerData?.PhoneNumber || "" },
        }))

        dispatch(setAddressInformation({
            PayerAddress: { value: payerData?.Address1 || "" },
            PayerAddress2: { value: payerData?.Address2 || "" },
            PayerCity: { value: payerData?.City || "" },
        }))

        dispatch(setOptionalInformation({
            DisregardedEntity: {
                value: payerData?.DisregardedEntity || "",
                errors: {},
                rules: { required: signedInUser?.MultiplePayerLocation ? true : false }
            },
            ClientID: {
                value: payerData?.ClientPayerId || "",
                errors: {},
                rules: { required: signedInUser?.IsPayerClient ? true : false, regex: { pattern: RESTRICT_SPACE_AND_SPECIAL_CHARACTERES_REGEX, message: SPACE_NOT_ALLOWED_IN_CLIENT_ID_TEXT } }
            },
            UnMaskRecipientTIN: { value: payerData?.UnMaskRecipientTin || false },
            StateID: { value: payerData?.Stateid || "" },
            // GroupID: { value: payerData?.ClientPayerId || "" },
        }))

        dispatch(setBasicInformationFieldsTouched(true))
        dispatch(setAddressInformationFieldsTouched(true))
        dispatch(setOptionalInformationFieldsTouched(true))
        dispatch(setZipCodeValidationSuccess(true));

        const country = countriesList?.find(country => country?.Country_code === payerData?.Country)

        let state = { label: payerData?.State, value: payerData?.State };
        if (payerData?.Country === "US") {
            state = statesList.find(state => state.StateAbbreviation === payerData?.State);
        }

        dispatch(setAddressInformation({ PayerCountry: country }))
        dispatch(setAddressInformation({ PayerState: state }))
        dispatch(setAddressInformation({ PayerZIP: { value: payerData?.ZipCode || "" } }))

        if (payerData?.Country === "US") {
            dispatch(setZipCodeValidationSuccess(false))
        } else {
            dispatch(setZipCodeValidationSuccess(true))
        }

    }, [payerData])

    useEffect(() => {
        if (payerUpdationSuccessful) {            

            if (isPopup) {
                setRefreshStep(0);
                onFinalSubmitCb();
                onPopupCloseCb();
            } 
        }
    }, [payerUpdationSuccessful])
    

    useEffect(() => {
        let firstName = '';
        let middleName = '';
        let lastName = '';
        if (payerID && !payerData?.PayerDetailId) {
            firstName = basicInfo?.PayerFirstName?.value || "";
            middleName = basicInfo?.PayerMiddleName?.value || "";
            lastName = basicInfo?.PayerLastName?.value || "";
        } else {
            firstName = payerData?.FirstName || "";
            middleName = payerData?.MiddleName || "";
            lastName = payerData?.LastName || "";
        }
        if (PayerType === PAYER_TYPE_TITLE.BUSINESS) {
            dispatch(setBasicInformation({
                PayerFirstName: {
                    value: firstName,
                    errors: {},
                    rules: { required: false }
                },
            }));

            dispatch(setBasicInformation({
                PayerMiddleName: {
                    value: middleName,
                    errors: {},
                    rules: { required: false }
                },
            }));
        }
        else if (PayerType === PAYER_TYPE_TITLE.INDIVIDUAL) {
            dispatch(setBasicInformation({
                PayerFirstName: {
                    value: firstName,
                    errors: {},
                    rules: { required: false, regex: { pattern: VALIDATE_NAME_REGEX, message: `A valid ${payerLabel} name should only consist any of A-Z,a-z, 0-9,-,.&' letters and maximum 40 character` } }
                },
            }));
            dispatch(setBasicInformation({
                PayerMiddleName: {
                    value: middleName,
                    errors: {},
                    rules: { required: false, regex: { pattern: VALIDATE_NAME_REGEX, message: `A valid ${payerLabel} name should only consist any of A-Z,a-z, 0-9,-,.&' letters and maximum 40 character` } }
                },
            }));
            dispatch(setBasicInformation({
                PayerLastName: {
                    value: lastName,
                    errors: {},
                    rules: { required: true, regex: { pattern: VALIDATE_NAME_REGEX, message: `A valid ${payerLabel} name should only consist any of A-Z,a-z, 0-9,-,.&' letters and maximum 40 character` } }
                },
            }));
        }
    }, [PayerType])

    useEffect(() => {
        if (addressInformation.PayerCountry.value !== "US") {
            dispatch(setAddressInformation({
                PayerState: {
                    rules: { required: false }
                },
            }));

            dispatch(setAddressInformation({
                PayerZIP: {
                    rules: { required: false, regex: { pattern: VALIDATE_ZIP_CODE_REGEX, message: "Please enter a valid ZIP Code. The ZIP Code should only contain numbers" } }
                }
            }));
        } else if (addressInformation.PayerCountry.value === "US") {
            dispatch(setAddressInformation({
                PayerState: {
                    rules: { required: true }
                },
            }));

            dispatch(setAddressInformation({
                PayerZIP: {
                    value: addressInformation?.PayerZIP?.value || payerData?.ZipCode || "",
                    rules: { required: true, regex: { pattern: VALIDATE_ZIP_CODE_REGEX, message: "Please enter a valid ZIP Code. The ZIP Code should only contain numbers" } }
                }
            }));
        }
    }, [addressInformation?.PayerCountry?.value, payerData?.ZipCode])

    const UPDATE_PAYER_STEPS = [
        {
            title: `Type of ${payerLabel}`,
            stepBodyComponent: <GetPayerType onCardClickCb={(card) => dispatch(setPayerType(card.title))} type={PayerType}
                is1042SForm={is1042SForm} />,
                ...(!isPopup
                    ? {
                        backButtonText: "Back",
                        prevCb: () => {
                          navigate(`/${PAYER_SECTION_ROUTES.PAYER}`);
                        },
                      }
                    : {}),
            
            submitButtonText: "Next",
            submitButtonDisabled: PayerType ? false : true,
            submitButtonDisabledInfoCb: () => {
                dispatch(addNotifications({ heading: `Please select the type of ${payerLabel}`, message: `To continue, please select the type of ${payerLabel}` }))
            },
            submitCb: () => { },
        },
        {
            title: "Basic information",
            stepBodyComponent: <GetBasicInfo payerLabel={payerLabel} />,
            backButtonText: "Back",
            prevCb: () => { },
            submitButtonText: "Next",
            submitButtonDisabled: GeneralValidator.validateRequiredFields(basicInfo) ? true : false,
            submitButtonDisabledInfoCb: () => {
                dispatch(setBasicInformationFieldsTouched(true))
                dispatch(addNotifications({ heading: "Error", message: "Please enter valid data" }))
            },
            submitCb: () => { return FEATURE_FLAG.VALIDATE_PAYER_RECIPIENT ? generalValidator.validateAllFieldsRules({ ...basicInfo, TIN: { ...basicInfo.TIN, value: tinValue, rules: tinRules } }, (basicInfo) => dispatch(setBasicInformation(basicInfo))) : true },
        },
        {
            title: "Address information",
            stepBodyComponent: <GetAddressInfo />,
            backButtonText: "Back",
            prevCb: () => { },
            submitButtonText: "Next",
            submitButtonDisabled: GeneralValidator.validateRequiredFields(addressInformation) || !zipCodeValidationSuccess ? true : false,
            submitButtonDisabledInfoCb: () => {
                dispatch(setAddressInformationFieldsTouched(true))

                if (GeneralValidator.validateRequiredFields(addressInformation)) {
                    dispatch(addNotifications({ heading: "Please enter valid data", message: "To continue, please enter the valid address data" }))
                } else if (!zipCodeValidationSuccess) {
                    dispatch(validateZipCode(addressInformation));
                }

            },
            submitCb: () => { },
        },
        {
            title: "Optional information",
            stepBodyComponent: <GetOptionalInfo payerLabel={payerLabel} />,
            backButtonText: "Back",
            prevCb: () => { },
            submitButtonText: "Next",
            submitButtonDisabled: GeneralValidator.validateRequiredFields(optionalInformation) ? true : false,
            submitButtonDisabledInfoCb: () => {
                dispatch(setOptionalInformationFieldsTouched(true))
                dispatch(addNotifications({ heading: "Please enter valid data", message: "To continue, please enter the valid data" }))
            },
            submitCb: () => fromNecGrid ? submitFormCb({ ...editPayerData, is1042SForm }) : dispatch(editPayer({ ...editPayerData, is1042SForm })),
        }
    ]

    const EditPayerJSX = (
        <div style={styles.payerContainer}>
            <Loadable currentState={loaderProps?.currentState ? loaderProps?.currentState : loadingState.state} loadingStates={loaderProps?.loadingStates ? loaderProps?.loadingStates : [ADD_PAYER_LOADING_STATES.EDIT_PAYER_LOADING, ADD_PAYER_LOADING_STATES.GET_PAYER_DATA_LOADING]} isNewLoadable={true} messageMap={loaderProps?.messageMap ? loaderProps?.messageMap : ADD_PAYER_LOADING_MESSAGE_MAP}>
                {payerUpdationSuccessful ?
                   ( <AddPayerSuccess setRefreshStep={setRefreshStep} isEdited={true} />)
                    : switchToAllStepsSingleView ? (<AllStepsSingleView steps={UPDATE_PAYER_STEPS} pageTitle={'Edit Payer'} onSwitchToOtherViewCb={() => setSwitchToAllStepsSingleView(!switchToAllStepsSingleView)} otherViewSwitchIsOpen={switchToAllStepsSingleView} otherViewSwitchLabel='Switch To Other View' />) :
                        (<StepperNavigation steps={UPDATE_PAYER_STEPS} refreshStep={refreshStep} setRefreshStep={(idx) => setRefreshStep(idx)} customStepperBodyContainerStyles={{ height: "auto" }}
                            customFooterBodyContainer={{ marginTop: "10px" }} showSwitchForOtherView={true} onSwitchToOtherViewCb={() => setSwitchToAllStepsSingleView(!switchToAllStepsSingleView)} otherViewSwitchLabel='Switch To Single View' applyMaxHeightToBodyContainer={"55vh"}
                        />)
                }
            </Loadable>
        </div>
    )

    return (
        <>
            {isPopup ?

                <Popup title={""} onCloseCb={onPopupCloseCb} isPopupOpen={isPopupOpen} customPopupSyles={styles.popupStyles} overlayStyles={styles.popupoOverlay} childrenStyles={styles.popupChildrenStyles}>
                    {EditPayerJSX}
                </Popup>

                :

                EditPayerJSX
            }
        </>
    )
}